import { join } from 'node:path'
import { readFileSync } from 'node:fs'
import type { EntityDefinition, ModuleManifest, PluginManifest } from '@gzl10/nexus-sdk'
import { moduleStore, type RegisteredModule } from './module-store.js'
import { getLibPath, getProjectPath } from '@kernel/config/paths.js'

/**
 * Application manifest containing modules and metadata
 */
export interface AppManifest {
  /** Package name */
  name: string
  /** Package version */
  version: string
  /** Registered modules */
  modules: ModuleManifest[]
  /** Registered plugins (only for user manifest) */
  plugins?: PluginManifest[]
}

interface PackageJson {
  name: string
  version: string
}

function readPackageJson(dir: string): PackageJson {
  const content = readFileSync(join(dir, 'package.json'), 'utf-8')
  const pkg = JSON.parse(content)
  return { name: pkg.name, version: pkg.version }
}

/**
 * Strip internal metadata from RegisteredModule
 */
function toModuleManifest(mod: RegisteredModule): ModuleManifest {
  const { _source, _pluginName, ...manifest } = mod
  return manifest as ModuleManifest
}

/**
 * Gets all registered modules
 */
export function getModules(): ModuleManifest[] {
  return moduleStore.modules.map(toModuleManifest)
}

/**
 * Sorts modules by dependencies (topological sort).
 * Ensures a module is processed after its dependencies.
 */
export function getOrderedModules(): ModuleManifest[] {
  return getOrderedModulesInternal().map(toModuleManifest)
}

/**
 * Internal version that preserves RegisteredModule metadata (_source, _disableEndpoints, etc.)
 * Used by module-routes.ts to check _disableEndpoints
 */
export function getOrderedModulesInternal(): RegisteredModule[] {
  const sorted: RegisteredModule[] = []
  const visited = new Set<string>()
  const moduleMap = new Map(moduleStore.modules.map(m => [m.name, m]))

  function visit(mod: RegisteredModule) {
    if (visited.has(mod.name)) return
    visited.add(mod.name)

    for (const dep of [...(mod.dependencies ?? []), ...(mod.optionalDependencies ?? [])]) {
      const depMod = moduleMap.get(dep)
      if (depMod) visit(depMod)
    }
    sorted.push(mod)
  }

  moduleStore.modules.forEach(visit)
  return sorted
}

/**
 * Gets a module by name
 */
export function getModule(name: string): ModuleManifest | undefined {
  const mod = moduleStore.modules.find(m => m.name === name)
  return mod ? toModuleManifest(mod) : undefined
}

/**
 * Gets a plugin by name
 */
export function getPlugin(name: string): PluginManifest | undefined {
  return moduleStore.plugins.get(name)
}

/**
 * Gets all registered plugins
 */
export function getPlugins(): PluginManifest[] {
  return [...moduleStore.plugins.values()]
}

/**
 * Gets all subjects registered in modules.
 * Includes 'all', which is always available.
 */
export function getRegisteredSubjects(): string[] {
  return [...moduleStore.subjects]
}

/**
 * Validates that a subject exists in a registered module
 */
export function isValidSubject(subject: string): boolean {
  return moduleStore.subjects.has(subject)
}

/**
 * Gets the CASL subject for a registered table name
 */
export function getSubjectForTable(table: string): string | undefined {
  return moduleStore.tableToSubject.get(table)
}

/**
 * Get core modules (registered by nexus-backend)
 */
export function getCoreModules(): ModuleManifest[] {
  return moduleStore.modules
    .filter(m => m._source === 'core')
    .map(toModuleManifest)
}

/**
 * Get user modules (registered via plugins or standalone)
 */
export function getUserModules(): ModuleManifest[] {
  return moduleStore.modules
    .filter(m => m._source !== 'core')
    .map(toModuleManifest)
}

/**
 * Get the core manifest (nexus-backend library)
 * Reads name/version from getLibPath()/package.json
 */
export function getCoreManifest(): AppManifest {
  const pkg = readPackageJson(getLibPath())
  return {
    name: pkg.name,
    version: pkg.version,
    modules: getCoreModules()
  }
}

/**
 * Get the user manifest (user's project)
 * Reads name/version from getProjectPath()/package.json
 * Returns null if getLibPath() === getProjectPath() (development mode)
 */
export function getUserManifest(): AppManifest | null {
  const libPath = getLibPath()
  const projectPath = getProjectPath()

  // In development mode (same path), there's no user manifest
  if (libPath === projectPath) {
    return null
  }

  const pkg = readPackageJson(projectPath)
  return {
    name: pkg.name,
    version: pkg.version,
    modules: getUserModules(),
    plugins: getPlugins()
  }
}

/**
 * Check if running as a library (user project exists)
 */
export function hasUserApp(): boolean {
  return getLibPath() !== getProjectPath()
}

/**
 * Checks if a module is registered by name
 */
export function hasModule(name: string): boolean {
  return moduleStore.modules.some(m => m.name === name)
}

/**
 * Checks if a plugin is registered by name
 */
export function hasPlugin(name: string): boolean {
  return moduleStore.plugins.has(name)
}

/**
 * Gets a plugin by its 3-char code
 */
export function getPluginByCode(code: string): PluginManifest | undefined {
  return moduleStore.pluginsByCode.get(code)
}

/**
 * Checks if a plugin is registered by its 3-char code
 */
export function hasPluginByCode(code: string): boolean {
  return moduleStore.pluginsByCode.has(code)
}

/**
 * Gets an entity definition by module name and entity identifier.
 *
 * Entity identifier matching:
 * - table === entityName (e.g. 'tags')
 * - table === '{moduleName}_{entityName}' (e.g. 'storage_files' for module 'storage', entity 'files')
 * - key === entityName (for single/config entities)
 */
export function getEntityDefinition(moduleName: string, entityName: string): EntityDefinition | undefined {
  const mod = moduleStore.modules.find(m => m.name === moduleName)
  if (!mod) return undefined

  const prefixedTable = `${moduleName}_${entityName}`

  for (const def of mod.definitions ?? []) {
    const d = def as Record<string, unknown>
    const table = d['table'] as string | undefined
    const key = d['key'] as string | undefined

    if (table === entityName || table === prefixedTable || key === entityName) {
      return def
    }
  }
  return undefined
}
